import { useEffect, useState } from "react";
import { Plus, Trash2, Search, Eye, Download, BellRing, Mail, MessageCircle, Paperclip, FileCheck2, Users, Pencil } from "lucide-react";
import { api, apiError, descargarFacturaPdf, facturaBlobUrl } from "@/lib/api";
import { money, fecha, norm } from "@/lib/format";
import { usePermisos } from "@/lib/permisos";
import { BuscadorEstudiante } from "@/components/BuscadorEstudiante";
import { BuscadorTutor } from "@/components/BuscadorTutor";
import { VentasSinFacturar } from "@/components/VentasSinFacturar";
import { LineasFactura, lineaLibre } from "@/components/LineasFactura";
import { EditarFactura } from "@/components/EditarFactura";
import { Loading, PageTitle } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminFacturacion() {
  const [facturas, setFacturas] = useState(null);
  const [estudiantes, setEstudiantes] = useState([]);
  const [tutores, setTutores] = useState([]);
  const [descuentos, setDescuentos] = useState([]);
  const [open, setOpen] = useState(false);
  const [filtro, setFiltro] = useState("todas");
  const [busqueda, setBusqueda] = useState("");
  const [form, setForm] = useState({
    modo: "consolidada", tutor_id: "", hijos: [], consumidor_final: false,
    estudiante_id: "", tipo: "mensualidad", concepto: "", monto: "", fecha_vencimiento: "",
    descuento_ids: [], ncf_manual: false, ncf: "", sin_valor_fiscal: false,
  });
  const [rncNuevo, setRncNuevo] = useState("");
  const [ncfSugerido, setNcfSugerido] = useState("");
  const [ver, setVer] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [enviando, setEnviando] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const [cuenta, setCuenta] = useState(null);
  const [ventasSel, setVentasSel] = useState([]);
  const [cobrarVentas, setCobrarVentas] = useState(true);
  const [editar, setEditar] = useState(null);
  const [extras, setExtras] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [combos, setCombos] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [descontarInv, setDescontarInv] = useState(true);
  const [inscribir, setInscribir] = useState(true);
  const { puede } = usePermisos();
  const activos = estudiantes.filter((e) => e.estado === "activo");
  const tutorSel = tutores.find((t) => t.id === form.tutor_id) || null;
  const hijosMarcados = form.hijos.filter((h) => h.marcado);
  const totalConsolidado = hijosMarcados.reduce((s, h) => s + (Number(h.monto) || 0), 0);

  const elegirTutor = (tid) => {
    const t = tutores.find((x) => x.id === tid);
    setRncNuevo(t?.cedula_rnc || "");
    setForm((f) => ({
      ...f, tutor_id: tid, consumidor_final: false,
      hijos: (t?.estudiantes || []).map((e) => ({
        estudiante_id: e.id, nombre: e.nombre, grado: e.grado, seccion: e.seccion,
        matricula: e.matricula, marcado: true, monto: f.monto || "",
      })),
    }));
  };

  const montoParaTodos = (valor) => setForm((f) => ({
    ...f, monto: valor, hijos: f.hijos.map((h) => ({ ...h, monto: valor })),
  }));

  const traerMensualidad = async () => {
    if (!form.tutor_id) return toast.error("Seleccione primero la familia");
    try {
      const { data } = await api.get("/mensualidades");
      const fam = data.familias.find((f) => f.tutor_id === form.tutor_id);
      const montos = Object.fromEntries((fam?.estudiantes || []).map((e) => [e.id, e.monto || 0]));
      if (!Object.values(montos).some((m) => m > 0)) {
        return toast.warning("Esta familia aún no tiene mensualidad configurada");
      }
      setForm((f) => ({
        ...f,
        concepto: f.concepto || fam.concepto || "",
        hijos: f.hijos.map((h) => ({
          ...h,
          monto: montos[h.estudiante_id] ? String(montos[h.estudiante_id]) : h.monto,
          marcado: montos[h.estudiante_id] > 0 ? true : h.marcado,
        })),
      }));
      toast.success("Montos traídos de la mensualidad de la familia");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarRnc = async () => {
    try {
      await api.put(`/facturacion/tutores/${form.tutor_id}/rnc`, { cedula_rnc: rncNuevo });
      setTutores((ts) => ts.map((t) => (t.id === form.tutor_id ? { ...t, cedula_rnc: rncNuevo } : t)));
      toast.success("Cédula/RNC guardada en la ficha de la familia");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/inventario").then((r) => setArticulos(r.data.articulos || []))
      .catch(() => setArticulos([]));
    api.get("/combos").then((r) => setCombos(r.data)).catch(() => setCombos([]));
    api.get("/actividades").then((r) => setActividades(r.data)).catch(() => setActividades([]));
  }, [open]);

  useEffect(() => {
    if (!open || form.modo !== "individual" || !form.estudiante_id) return setCuenta(null);
    api.get(`/estudiantes/${form.estudiante_id}/cuenta-resumen`)
      .then((r) => setCuenta(r.data)).catch(() => setCuenta(null));
  }, [open, form.modo, form.estudiante_id]);

  const visualizar = async (f) => {
    setVer(f);
    setPdfUrl(null);
    try {
      setPdfUrl(await facturaBlobUrl(f.id));
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el comprobante"));
    }
  };

  const descargar = async (f) => {
    try {
      await descargarFacturaPdf(f.id, f.ncf);
    } catch (e) {
      toast.error(apiError(e, "No se pudo descargar el comprobante"));
    }
  };

  const recordar = async (f) => {
    try {
      await api.post(`/facturas/${f.id}/recordatorio`);
      toast.success(`Recordatorio de pago enviado a ${f.tutor_nombre || "el tutor"}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enviarCorreo = async (f) => {
    setEnviando(f.id);
    try {
      const { data } = await api.post(`/facturas/${f.id}/enviar-correo`);
      toast.success(`Factura ${f.ncf} enviada a ${data.enviado_a}`);
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar el correo"));
    }
    setEnviando(null);
  };

  const enviarWhatsapp = async (f) => {
    try {
      const { data } = await api.get(`/facturas/${f.id}/whatsapp`);
      window.open(data.url, "_blank");
      if (!data.telefono) toast.info("El tutor no tiene teléfono registrado: elija el contacto en WhatsApp");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminarFactura = async (f) => {
    try {
      await api.delete(`/facturas/${f.id}`);
      toast.success(`Factura ${f.ncf} eliminada`);
      setConfirmar(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const anularFactura = async (f) => {
    try {
      await api.put(`/facturas/${f.id}/anular`);
      toast.success(`Factura ${f.ncf} anulada`);
      setConfirmar(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cargar = () => {
    api.get("/facturas").then((r) => setFacturas(r.data));
    api.get("/estudiantes").then((r) => setEstudiantes(r.data));
    api.get("/facturacion/tutores").then((r) => setTutores(r.data)).catch(() => setTutores([]));
    api.get("/descuentos").then((r) => setDescuentos(r.data));
    api.get("/ncf/siguiente").then((r) => setNcfSugerido(r.data.sugerido)).catch(() => {});
  };

  useEffect(cargar, []);

  const cuerpoFactura = () => {
    const base = {
      tipo: form.tipo,
      fecha_vencimiento: new Date(form.fecha_vencimiento).toISOString(),
      descuento_ids: form.descuento_ids,
      ncf: form.ncf_manual && !form.sin_valor_fiscal ? form.ncf.trim() : null,
      fiscal: !form.sin_valor_fiscal,
      consumidor_final: form.consumidor_final,
      venta_ids: ventasSel,
      marcar_pagada: ventasSel.length > 0 && cobrarVentas,
      lineas_extra: extras.filter((l) => l.concepto.trim() && Number(l.monto_unitario) > 0)
        .map((l) => ({
          concepto: l.concepto.trim(), monto_unitario: Number(l.monto_unitario),
          cantidad: Number(l.cantidad) || 1, articulo_id: l.articulo_id,
          combo_id: l.combo_id, actividad_id: l.actividad_id, categoria: l.categoria,
        })),
      descontar_inventario: descontarInv,
      inscribir,
    };
    const conConcepto = !!form.concepto.trim();
    if (form.modo === "consolidada") {
      return {
        ...base, tutor_id: form.tutor_id,
        estudiantes: hijosMarcados.map((h) => ({
          estudiante_id: h.estudiante_id, concepto: form.concepto,
          monto_unitario: conConcepto ? Number(h.monto) || 0 : 0, cantidad: 1,
        })),
      };
    }
    return {
      ...base, estudiante_id: form.estudiante_id,
      detalle: conConcepto && Number(form.monto) > 0
        ? [{ concepto: form.concepto, monto_unitario: Number(form.monto), cantidad: 1 }]
        : [],
    };
  };

  const validar = () => {
    const conLineas = !!form.concepto.trim() && Number(form.monto) > 0;
    const conExtras = extras.some((l) => l.concepto.trim() && Number(l.monto_unitario) > 0);
    if (!conLineas && !conExtras && ventasSel.length === 0) {
      return "Indique el concepto y el monto, agregue otros conceptos o marque alguna venta";
    }
    if (extras.some((l) => l.concepto.trim() && !(Number(l.monto_unitario) > 0))) {
      return "Indique un precio mayor a cero en cada concepto agregado";
    }
    if (form.concepto.trim() && !(Number(form.monto) > 0)) {
      return "Indique un monto mayor a cero para el concepto escrito";
    }
    if (!form.fecha_vencimiento) return "Seleccione la fecha de vencimiento";
    if (form.ncf_manual && !form.sin_valor_fiscal && !form.ncf.trim()) {
      return "Escriba el número de comprobante fiscal";
    }
    if (form.modo === "consolidada") {
      if (!form.tutor_id) return "Busque y seleccione la familia (tutor)";
      if (hijosMarcados.length === 0) return "Marque al menos un hijo para facturar";
      if (conLineas && hijosMarcados.some((h) => !h.monto || Number(h.monto) <= 0)) {
        return "Indique el monto de cada hijo marcado";
      }
      return null;
    }
    if (!form.estudiante_id) return "Busque y seleccione el estudiante";
    return null;
  };

  const emitir = async () => {
    const error = validar();
    if (error) return toast.error(error);
    try {
      const { data } = await api.post("/facturas", cuerpoFactura());
      toast.success(data.fiscal
        ? `Factura ${data.ncf} emitida por ${money(data.monto_total)}`
        : `Factura SIN valor fiscal ${data.ncf} emitida por ${money(data.monto_total)}`);
      (data.avisos_inventario || []).forEach((a) => toast.warning(a));
      setOpen(false);
      setVentasSel([]);
      setExtras([]);
      setForm({ ...form, concepto: "", monto: "", ncf_manual: false, ncf: "",
                sin_valor_fiscal: false, hijos: form.hijos.map((h) => ({ ...h, monto: "" })) });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const convertirFiscal = async (f) => {
    try {
      const { data } = await api.post(`/facturas/${f.id}/convertir-fiscal`);
      toast.success(`Comprobante fiscal ${data.ncf} asignado`);
      setConfirmar(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmarPago = async (f) => {
    try {
      await api.post(`/facturas/${f.id}/confirmar-pago`);
      toast.success(`Pago confirmado. El tutor ya ve la factura ${f.ncf} como pagada`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rechazarPago = async (f) => {
    const motivo = window.prompt("Motivo del rechazo (lo verá el tutor):",
      "El comprobante no coincide con el monto de la factura.");
    if (motivo === null) return;
    try {
      await api.post(`/facturas/${f.id}/rechazar-pago`, { motivo });
      toast.success("Pago rechazado y tutor notificado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verComprobantePago = async (f) => {
    try {
      const { data } = await api.get(`/facturas/${f.id}/comprobante-pago`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) {
      toast.error(apiError(e, "Esta factura no tiene comprobante adjunto"));
    }
  };

  const marcarPagada = async (f) => {
    try {
      await api.post(`/facturas/${f.id}/pagar`, { metodo: "transferencia" });
      toast.success("Pago registrado en caja");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!facturas) return <Loading />;

  const visibles = facturas.filter((f) => {
    if (filtro !== "todas" && f.estado !== filtro) return false;
    const q = norm(busqueda.trim());
    if (!q) return true;
    return [f.ncf, f.estudiante_nombre, f.tutor_nombre, f.tipo, f.detalle?.[0]?.concepto]
      .filter(Boolean)
      .some((v) => norm(v).includes(q));
  });
  const totalVisible = visibles.reduce((s, f) => s + f.monto_total, 0);

  return (
    <div data-testid="admin-facturacion">
      <PageTitle title="Facturación"
                 subtitle="Comprobantes fiscales con NCF secuencial, facturas sin valor fiscal, descuentos y registro de cobros.">
        <Button className="rounded-full" data-testid="nueva-factura"
                onClick={() => { setForm({ ...form, estudiante_id: "" }); setOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Emitir factura
        </Button>
      </PageTitle>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-10 rounded-full"
            placeholder="Buscar por NCF, estudiante, tutor o concepto…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            data-testid="buscar-factura"
          />
        </div>
        {["todas", "pendiente", "en_verificacion", "pagado", "anulado"].map((f) => (
          <button key={f} onClick={() => setFiltro(f)} data-testid={`filtro-${f}`}
                  className={`rounded-full border px-4 py-2 text-sm capitalize lift ${
                    filtro === f ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
            {f === "en_verificacion" ? "en verificación" : f}
          </button>
        ))}
        <span className="text-xs text-muted-foreground" data-testid="contador-facturas">
          {visibles.length} factura(s) · {money(totalVisible)}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm" data-testid="tabla-admin-facturas">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Comprobante</th>
                <th className="px-6 py-3">Estudiante</th>
                <th className="px-6 py-3">Cliente (tutor)</th>
                <th className="px-6 py-3">Tipo</th>
                <th className="px-6 py-3">Vence</th>
                <th className="px-6 py-3">Total</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (                <tr key={f.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-6 py-4 font-mono text-xs">
                    {f.ncf}
                    {f.fiscal === false && (
                      <Badge variant="outline" className="ml-2 border-destructive/40 text-[10px] text-destructive"
                             data-testid={`sin-valor-fiscal-${f.ncf}`}>
                        sin valor fiscal
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {f.estudiantes_nombres?.length > 1 ? (
                      <span data-testid={`consolidada-${f.ncf}`}>
                        <span className="block text-xs font-semibold text-primary">
                          Consolidada · {f.estudiantes_nombres.length} estudiantes
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {f.estudiantes_nombres.join(", ")}
                        </span>
                      </span>
                    ) : f.estudiante_nombre}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">
                    {f.cliente_nombre || f.tutor_nombre}
                    {(f.cliente_rnc || f.tutor_rnc) && (
                      <span className="block font-mono text-[10px]">{f.cliente_rnc || f.tutor_rnc}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 capitalize">{f.tipo}</td>
                  <td className="px-6 py-4 font-mono text-xs">{fecha(f.fecha_vencimiento)}</td>
                  <td className="px-6 py-4 font-mono font-semibold">{money(f.monto_total)}</td>
                  <td className="px-6 py-4">
                    <Badge variant={f.estado === "pagado" ? "secondary"
                      : f.estado === "en_verificacion" ? "default" : "outline"}>
                      {f.estado === "en_verificacion" ? "en verificación" : f.estado}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      {f.estado === "en_verificacion" && puede("facturas:cobrar") && (
                        <>
                          <Button size="sm" className="rounded-full" onClick={() => confirmarPago(f)}
                                  data-testid={`aprobar-pago-${f.ncf}`}>
                            Aprobar pago
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full"
                                  onClick={() => rechazarPago(f)} data-testid={`rechazar-pago-${f.ncf}`}>
                            Rechazar
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => verComprobantePago(f)}
                                  title="Ver comprobante de transferencia"
                                  data-testid={`ver-comprobante-${f.ncf}`}>
                            <Paperclip className="h-3 w-3 text-primary" />
                          </Button>
                        </>
                      )}
                      {f.estado === "pendiente" && (
                        <>
                          <Button size="sm" variant="outline" className="rounded-full"
                                  onClick={() => marcarPagada(f)} data-testid={`registrar-pago-${f.ncf}`}>
                            Registrar pago
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => recordar(f)} title="Enviar recordatorio"
                                  data-testid={`recordatorio-${f.ncf}`}>
                            <BellRing className="h-3 w-3 text-accent" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => enviarCorreo(f)} title="Enviar por correo"
                              disabled={enviando === f.id} data-testid={`correo-factura-${f.ncf}`}>
                        <Mail className="h-3 w-3 text-primary" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => enviarWhatsapp(f)} title="Enviar por WhatsApp"
                              data-testid={`whatsapp-factura-${f.ncf}`}>
                        <MessageCircle className="h-3 w-3 text-primary" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => visualizar(f)} title="Ver factura"
                              data-testid={`ver-factura-${f.ncf}`}>
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => descargar(f)} title="Descargar PDF"
                              data-testid={`descargar-factura-${f.ncf}`}>
                        <Download className="h-3 w-3" />
                      </Button>
                      {f.fiscal === false && puede("facturas:crear") && (
                        <Button size="sm" variant="outline" className="rounded-full"
                                onClick={() => convertirFiscal(f)} data-testid={`convertir-fiscal-${f.ncf}`}
                                title="Asignar NCF y convertir en comprobante fiscal">
                          <FileCheck2 className="mr-1 h-3 w-3" /> Hacer fiscal
                        </Button>
                      )}
                      {puede("facturas:crear") && f.estado !== "anulado" && (
                        <Button size="sm" variant="ghost" onClick={() => setEditar(f)}
                                title="Editar líneas de la factura"
                                data-testid={`editar-factura-${f.ncf}`}>
                          <Pencil className="h-3 w-3 text-primary" />
                        </Button>
                      )}
                      {(puede("facturas:anular") || puede("facturas:eliminar")) && (
                        <Button size="sm" variant="ghost" onClick={() => setConfirmar(f)} title="Anular o eliminar"
                                data-testid={`eliminar-factura-${f.ncf}`}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibles.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground" data-testid="facturas-vacias">
              Ninguna factura coincide con la búsqueda.
            </p>
          )}
        </div>
      </div>

      <Dialog open={!!ver} onOpenChange={(o) => { if (!o) { setVer(null); setPdfUrl(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="modal-ver-factura">
          <DialogHeader>
            <DialogTitle className="font-display">
              {ver?.fiscal === false ? `Factura sin valor fiscal ${ver?.ncf}` : `Comprobante fiscal ${ver?.ncf}`}
            </DialogTitle>
            <DialogDescription>
              {ver?.estudiante_nombre} · {money(ver?.monto_total)} · {ver?.estado}
            </DialogDescription>
          </DialogHeader>
          {pdfUrl ? (
            <>
              <iframe src={pdfUrl} title={`Factura ${ver?.ncf}`} data-testid="visor-factura-admin"
                      className="h-[65vh] w-full rounded-md border border-border" />
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="abrir-nueva-pestana-admin"
                 className="text-center text-xs text-primary hover:underline">
                Abrir el comprobante en una pestaña nueva
              </a>
            </>
          ) : (
            <div className="flex h-[65vh] items-center justify-center text-sm text-muted-foreground">
              Generando comprobante…
            </div>
          )}
          <Button variant="outline" className="w-full rounded-full" onClick={() => descargar(ver)}
                  data-testid="descargar-desde-visor-admin">
            <Download className="mr-2 h-4 w-4" /> Descargar PDF
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmar} onOpenChange={(o) => !o && setConfirmar(null)}>
        <DialogContent data-testid="modal-eliminar-factura">
          <DialogHeader>
            <DialogTitle className="font-display">Anular o eliminar factura</DialogTitle>
            <DialogDescription>
              {confirmar?.ncf} · {confirmar?.estudiante_nombre} · {money(confirmar?.monto_total)}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Anular mantiene el comprobante en el histórico marcado como anulado (recomendado para fines fiscales).
            Eliminar borra el registro y sus pagos asociados de forma permanente.
          </p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            {puede("facturas:anular") && (
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => anularFactura(confirmar)}
                      data-testid="confirmar-anular">Anular factura</Button>
            )}
            {puede("facturas:eliminar") && (
              <Button variant="destructive" className="flex-1 rounded-full" onClick={() => eliminarFactura(confirmar)}
                      data-testid="confirmar-eliminar">Eliminar definitivamente</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-nueva-factura">
          <DialogHeader>
            <DialogTitle className="font-display">Emitir factura</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="flex gap-1 rounded-full border border-border bg-muted/40 p-1">
              {[["consolidada", "Consolidada por tutor"], ["individual", "Individual por estudiante"]]
                .map(([v, label]) => (
                <button key={v} type="button" data-testid={`modo-${v}`}
                        onClick={() => setForm({ ...form, modo: v })}
                        className={`flex-1 rounded-full px-4 py-2 text-sm lift ${
                          form.modo === v ? "bg-primary font-semibold text-primary-foreground"
                                          : "text-muted-foreground hover:bg-muted"}`}>
                  {label}
                </button>
              ))}
            </div>

            {form.modo === "consolidada" ? (
              <div className="space-y-3">
                <Label>Familia · responsable de pago</Label>
                <BuscadorTutor tutores={tutores} valor={form.tutor_id} testId="factura-tutor"
                               onChange={elegirTutor} />
                {tutorSel && (
                  <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Cédula o RNC del cliente
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        <Input value={rncNuevo} placeholder="001-1234567-8" className="flex-1 font-mono"
                               data-testid="factura-rnc-tutor"
                               onChange={(e) => setRncNuevo(e.target.value)} />
                        <Button size="sm" variant="outline" className="rounded-full"
                                onClick={guardarRnc} data-testid="guardar-rnc-tutor">
                          Guardar en la ficha
                        </Button>
                      </div>
                      {!tutorSel.cedula_rnc && (
                        <label className="flex items-start gap-3 text-xs">
                          <Checkbox checked={form.consumidor_final} data-testid="factura-consumidor-final"
                                    onCheckedChange={(v) => setForm({ ...form, consumidor_final: !!v })} />
                          <span>
                            Emitir como <b>consumidor final</b> (sin cédula/RNC en el comprobante)
                          </span>
                        </label>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        Hijos a incluir ({hijosMarcados.length} de {form.hijos.length})
                      </p>
                      {form.hijos.map((h, i) => (
                        <div key={h.estudiante_id} className="flex flex-wrap items-center gap-3 rounded-md bg-card p-2">
                          <Checkbox checked={h.marcado} data-testid={`hijo-${h.matricula}`}
                                    onCheckedChange={(v) => setForm({
                                      ...form,
                                      hijos: form.hijos.map((x, j) => (j === i ? { ...x, marcado: !!v } : x)),
                                    })} />
                          <span className="min-w-0 flex-1 text-sm">
                            <span className="block truncate font-medium">{h.nombre}</span>
                            <span className="block truncate font-mono text-xs text-muted-foreground">
                              {h.matricula} · {h.grado}{h.seccion ? ` ${h.seccion}` : ""}
                            </span>
                          </span>
                          <Input type="number" value={h.monto} className="w-32" placeholder="Monto"
                                 data-testid={`monto-hijo-${h.matricula}`}
                                 onChange={(e) => setForm({
                                   ...form,
                                   hijos: form.hijos.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)),
                                 })} />
                          <Button size="sm" variant="ghost" data-testid={`quitar-hijo-${h.matricula}`}
                                  title={h.marcado ? "Quitar de esta factura" : "Volver a incluir"}
                                  onClick={() => setForm({
                                    ...form,
                                    hijos: form.hijos.map((x, j) => (j === i
                                      ? { ...x, marcado: !x.marcado, monto: x.marcado ? "" : x.monto }
                                      : x)),
                                  })}>
                            {h.marcado ? "Quitar" : "Incluir"}
                          </Button>
                        </div>
                      ))}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold" data-testid="total-consolidado">
                          <Users className="mr-2 inline h-4 w-4 text-primary" />
                          Total de la factura: {money(totalConsolidado)}
                        </p>
                        <Button size="sm" variant="outline" className="rounded-full"
                                onClick={traerMensualidad} data-testid="traer-mensualidad">
                          Traer montos de la mensualidad
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Estudiante</Label>
                <BuscadorEstudiante estudiantes={activos} valor={form.estudiante_id}
                                    testId="factura-estudiante"
                                    onChange={(id) => setForm({ ...form, estudiante_id: id })} />
                {cuenta && (
                  <p className="text-xs text-muted-foreground" data-testid="factura-cuenta-estudiante">
                    {cuenta.pendientes > 0
                      ? `Tiene ${cuenta.pendientes} factura(s) pendiente(s) por ${money(cuenta.balance)}`
                      : "Cuenta al día"}
                    {cuenta.vencidas > 0 ? ` · ${cuenta.vencidas} vencida(s)` : ""}
                  </p>
                )}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                        data-testid="factura-tipo">
                  {["mensualidad", "inscripcion", "combo", "actividad"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Vencimiento</Label>
                <Input type="date" value={form.fecha_vencimiento} data-testid="factura-vencimiento"
                       onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Concepto</Label>
              <Input placeholder="Mensualidad Diciembre 2025" value={form.concepto} data-testid="factura-concepto"
                     onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{form.modo === "consolidada" ? "Monto por estudiante (RD$)" : "Monto (RD$)"}</Label>
              <Input type="number" value={form.monto} data-testid="factura-monto"
                     onChange={(e) => (form.modo === "consolidada"
                       ? montoParaTodos(e.target.value)
                       : setForm({ ...form, monto: e.target.value }))} />
              {form.modo === "consolidada" && (
                <p className="text-xs text-muted-foreground">
                  Se aplica a todos los hijos marcados; puede ajustar el monto de cada uno arriba.
                </p>
              )}
            </div>
            <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4"
                 data-testid="otros-conceptos">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  Otros conceptos a cobrar (uniformes, libros, materiales, combos y actividades)
                </Label>
                {extras.length === 0 && (
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid="abrir-otros-conceptos"
                          onClick={() => setExtras([{ ...lineaLibre }])}>
                    <Plus className="mr-2 h-3 w-3" /> Agregar concepto
                  </Button>
                )}
              </div>
              {extras.length > 0 && (
                <>
                  <LineasFactura lineas={extras} onChange={setExtras} articulos={articulos}
                                 combos={combos} actividades={actividades} />
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={descontarInv} data-testid="factura-descontar-inventario"
                              onCheckedChange={(v) => setDescontarInv(!!v)} />
                    <span>
                      <span className="font-semibold">Descontar del inventario al emitir</span>
                      <span className="block text-xs text-muted-foreground">
                        Si lo desmarca, la existencia se descuenta cuando el tutor pague la factura.
                        Le aviso si algún artículo queda por debajo del mínimo.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={inscribir} data-testid="factura-inscribir"
                              onCheckedChange={(v) => setInscribir(!!v)} />
                    <span>
                      <span className="font-semibold">
                        Inscribir en las actividades y combos cobrados
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        El estudiante queda inscrito al emitir la factura y la familia lo ve en su
                        portal.
                      </span>
                    </span>
                  </label>
                </>
              )}
            </div>
            <VentasSinFacturar
              tutorId={form.modo === "consolidada" ? form.tutor_id : null}
              estudianteId={form.modo === "individual" ? form.estudiante_id : null}
              seleccion={ventasSel} onSeleccion={setVentasSel}
              marcarPagada={cobrarVentas} onMarcarPagada={setCobrarVentas} />
            <div className="space-y-3">
              <Label>Descuentos y recargos aplicables</Label>              {descuentos.map((d) => (
                <label key={d.id} className="flex items-center gap-3 text-sm">
                  <Checkbox
                    checked={form.descuento_ids.includes(d.id)}
                    data-testid={`factura-descuento-${d.id}`}
                    onCheckedChange={(v) =>
                      setForm({
                        ...form,
                        descuento_ids: v
                          ? [...form.descuento_ids, d.id]
                          : form.descuento_ids.filter((x) => x !== d.id),
                      })
                    } />
                  {d.nombre}
                  <span className={`font-mono text-xs font-semibold ${
                    d.recargo ? "text-destructive" : "text-primary"}`}>
                    {d.recargo ? "+" : "−"}
                    {d.tipo === "porcentaje" ? `${d.valor}%` : money(d.valor)}
                  </span>
                  <span className="text-muted-foreground">({d.aplicable_a})</span>
                </label>
              ))}
            </div>
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
              {puede("facturas:sin_valor_fiscal") && (
                <label className="flex items-start gap-3 text-sm">
                  <Checkbox checked={form.sin_valor_fiscal} data-testid="factura-sin-valor-fiscal"
                            onCheckedChange={(v) => setForm({ ...form, sin_valor_fiscal: !!v })} />
                  <span>
                    <span className="font-semibold">Emitir SIN valor fiscal</span>
                    <span className="block text-xs text-muted-foreground">
                      No consume NCF: usa la numeración propia (SV-…) y el PDF sale sellado como
                      “SIN VALOR FISCAL · No válido para fines fiscales de la DGII”.
                    </span>
                  </span>
                </label>
              )}
              {!form.sin_valor_fiscal && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>Comprobante fiscal (NCF)</Label>
                    <span className="font-mono text-xs text-muted-foreground" data-testid="ncf-sugerido">
                      Automático: {ncfSugerido || "—"}
                    </span>
                  </div>
                  <label className="flex items-center gap-3 text-sm">
                    <Checkbox checked={form.ncf_manual} data-testid="factura-ncf-manual"
                              onCheckedChange={(v) => setForm({ ...form, ncf_manual: !!v })} />
                    Asignar el NCF manualmente
                  </label>
                  {form.ncf_manual ? (
                    <Input value={form.ncf} placeholder={ncfSugerido || "B0100000001"} data-testid="factura-ncf"
                           className="font-mono" onChange={(e) => setForm({ ...form, ncf: e.target.value })} />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      El sistema tomará el siguiente número de la secuencia configurada.
                    </p>
                  )}
                </>
              )}
            </div>
            <Button className="rounded-full py-6" onClick={emitir} data-testid="emitir-factura">
              {form.sin_valor_fiscal ? "Emitir factura sin valor fiscal" : "Emitir factura con NCF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <EditarFactura factura={editar} onClose={() => setEditar(null)} onGuardado={cargar} />
    </div>
  );
}
